import * as React from "react";

/* ── icons ─────────────────────────────────────────────────────────── */
const IconSearch  = () => <span className="e-icons e-search"    style={{ fontSize: 13 }} />;
const IconClear   = () => <span className="e-icons e-close"     style={{ fontSize: 11 }} />;
const IconChevron = ({ open }) => (
  <span
    className={"e-icons e-chevron-right"}
    style={{
      fontSize: 11,
      display: "inline-block",
      transition: "transform .18s",
      transform: open ? "rotate(90deg)" : "rotate(0deg)",
    }}
  />
);

/* ── helpers ────────────────────────────────────────────────────────── */
const normalize = (s) => String(s ?? "").toLowerCase();
const toKey = (v) => String(v ?? "");

function groupItems(values) {
  const map = {};
  (values || []).forEach((item) => {
    const g = item.Groupname || item.GroupName || item.Groupcondition || "Sistema";
    if (!map[g]) map[g] = [];
    map[g].push(item);
  });
  return Object.entries(map).sort(([a], [b]) => {
    if (a === "Sistema") return -1;
    if (b === "Sistema") return 1;
    return String(a).localeCompare(String(b));
  });
}

const GROUP_LABELS = {
  "Sistema":      "Sistema",
  "CUSTOM PAGES": "Páginas personalizadas",
  "CUSTOM MENU":  "Menú personalizado",
};

/* ── component ─────────────────────────────────────────────────────── */
const CompGroupedPermission = ({ name, label, values = [], value = [], onChange }) => {
  const buildSet = (arr) => new Set((arr || []).map(toKey));

  const [selected, setSelected] = React.useState(() => buildSet(value));

  /* Keep ref always current so native listeners don't have stale closures */
  const selectedRef = React.useRef(selected);
  selectedRef.current = selected;

  /* Sync from props when value changes. Compares content to avoid resetting
     user selections if a parent re-render passes a new array with same data. */
  const prevValueRef = React.useRef(value);
  React.useEffect(() => {
    if (value === prevValueRef.current) return;
    prevValueRef.current = value;
    const newSet = buildSet(value);
    const cur = selectedRef.current;
    if (newSet.size === cur.size && [...newSet].every((k) => cur.has(k))) return;
    setSelected(newSet);
  }, [value]);

  const [search,  setSearch]  = React.useState("");
  const [openMap, setOpenMap] = React.useState(() => {
    const init = {};
    groupItems(values).forEach(([g]) => { init[g] = true; });
    return init;
  });

  /* Stable refs for state setters used in native listeners */
  const openMapRef    = React.useRef(openMap);
  openMapRef.current  = openMap;
  const setOpenMapRef = React.useRef(setOpenMap);
  const setSearchRef  = React.useRef(setSearch);

  const q = normalize(search);
  const grouped = React.useMemo(() => {
    if (!q) return groupItems(values);
    return groupItems(values.filter((v) => normalize(v.Name).includes(q)));
  }, [values, q]);

  /* ── emit: preserve original Code types ──────────────────────────── */
  const emit = (nextSet) => {
    if (!onChange) return;
    const codeMap = {};
    (values || []).forEach((item) => { codeMap[toKey(item.Code)] = item.Code; });
    const result = [...nextSet].map((k) => (k in codeMap ? codeMap[k] : k));
    onChange({ target: { name, value: result } });
  };

  const updateSelected = (next) => {
    setSelected(next);
  };

  /* ── Stable toggle functions (use selectedRef so no stale closures) ── */
  const toggleItemByKey = React.useCallback((key) => {
    const prev = selectedRef.current;
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    updateSelected(next);
    emit(next);
  });

  const toggleGroupItems = React.useCallback((items) => {
    const keys = items.map((i) => toKey(i.Code));
    const prev = selectedRef.current;
    const allSel = keys.every((k) => prev.has(k));
    const next = new Set(prev);
    allSel ? keys.forEach((k) => next.delete(k)) : keys.forEach((k) => next.add(k));
    updateSelected(next);
    emit(next);
  });

  /* ── Native event delegation ──────────────────────────────────────
     Syncfusion DialogComponent (isModal=true) renders content outside the
     React root container in some versions. React 17+ attaches event delegation
     to the root container, not document, so synthetic events (onClick, onChange)
     don't fire inside EJ2 dialog portals. We use native addEventListener instead. */
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    /* Single click handler for all interactions */
    const onNativeClick = (e) => {
      /* ── Checkbox toggle ── */
      const cb = e.target.closest('input[type="checkbox"][data-cgp-code]');
      if (cb) {
        /* Derive next state from React's selected set (cb.checked is unreliable
           when the checkbox is a React-controlled input with no onChange) */
        const key = cb.dataset.cgpCode;
        const prev = selectedRef.current;
        const next = new Set(prev);
        prev.has(key) ? next.delete(key) : next.add(key);
        updateSelected(next);
        emit(next);
        return;
      }

      /* ── "Todos" / "Quitar" button ── */
      const toggleBtn = e.target.closest('[data-cgp-group]');
      if (toggleBtn) {
        e.stopPropagation();
        const groupName = toggleBtn.dataset.cgpGroup;
        const all = groupItems(values);
        const entry = all.find(([g]) => String(g) === groupName);
        if (entry) toggleGroupItems(entry[1]);
        return;
      }

      /* ── Group header (expand / collapse) ── */
      const header = e.target.closest('[data-cgp-header]');
      if (header) {
        const groupName = header.dataset.cgpHeader;
        setOpenMapRef.current((m) => ({ ...m, [groupName]: !m[groupName] }));
      }
    };

    /* Search input via native input event */
    const onNativeInput = (e) => {
      if (e.target.classList.contains("cgp-search-input")) {
        setSearchRef.current(e.target.value);
      }
    };

    /* Search clear button */
    const onNativeClearClick = (e) => {
      const clearBtn = e.target.closest(".cgp-search-clear");
      if (clearBtn) {
        setSearchRef.current("");
        /* Manually clear the native input value */
        const inp = el.querySelector(".cgp-search-input");
        if (inp) inp.value = "";
      }
    };

    el.addEventListener("click", onNativeClick);
    el.addEventListener("input", onNativeInput);

    return () => {
      el.removeEventListener("click", onNativeClick);
      el.removeEventListener("input", onNativeInput);
    };
  }, [values]); // values is stable across re-renders

  const totalSelected = selected.size;
  const totalItems    = values.length;

  return (
    <div className="cgp-root" ref={rootRef}>
      <div className="cgp-header">
        <span className="cgp-label">{label}</span>
        {totalSelected > 0 && (
          <span className="cgp-badge-total">{totalSelected} / {totalItems}</span>
        )}
      </div>

      <div className="cgp-search-bar">
        <IconSearch />
        {/* value is NOT controlled here — native input event drives search state */}
        <input
          className="cgp-search-input"
          type="search"
          placeholder="Buscar..."
          defaultValue={search}
          autoComplete="off"
          data-form-type="other"
        />
        {search && (
          <button type="button" className="cgp-search-clear">
            <IconClear />
          </button>
        )}
      </div>

      <div className="cgp-list">
        {grouped.length === 0 && (
          <div className="cgp-empty">Sin resultados para "{search}"</div>
        )}

        {grouped.map(([group, items]) => {
          const selectedInGroup = items.filter((i) => selected.has(toKey(i.Code))).length;
          const allSelected     = selectedInGroup === items.length;
          const isOpen          = openMap[group] !== false;

          return (
            <div key={group} className="cgp-group">
              {/* data-cgp-header drives expand/collapse via native click */}
              <div
                className={"cgp-group-header" + (allSelected ? " cgp-group-header--all" : "")}
                data-cgp-header={group}
              >
                <IconChevron open={isOpen} />
                <span className="cgp-group-name">{GROUP_LABELS[group] || group}</span>
                <span className="cgp-group-count">
                  <span className={selectedInGroup > 0 ? "cgp-count-sel" : "cgp-count-zero"}>
                    {selectedInGroup}
                  </span>
                  <span className="cgp-count-sep"> / </span>
                  <span className="cgp-count-total">{items.length}</span>
                </span>
                {/* data-cgp-group drives toggleGroup via native click (stopPropagation in handler) */}
                <button
                  type="button"
                  className="cgp-toggle-all"
                  data-cgp-group={group}
                  title={allSelected ? "Quitar todos" : "Seleccionar todos"}
                >
                  {allSelected ? "Quitar" : "Todos"}
                </button>
              </div>

              {isOpen && (
                <div className="cgp-items">
                  {items.map((item) => {
                    const key     = toKey(item.Code);
                    const checked = selected.has(key);
                    return (
                      <label
                        key={key}
                        className={"cgp-item" + (checked ? " cgp-item--checked" : "")}
                      >
                        {/* data-cgp-code drives toggle via native click listener */}
                        <input
                          type="checkbox"
                          className="cgp-checkbox"
                          data-cgp-code={key}
                          checked={checked}
                          onChange={() => {}} /* suppress React warning; interaction via native listener */
                        />
                        <span className="cgp-item-name">{item.Name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompGroupedPermission;
